import { Injectable, inject } from "@angular/core";
import { flatMap } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import {
    debounceTime,
    filter,
    map,
    takeUntil,
    tap
} from "rxjs/operators";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { GenericMessageRow } from "../arbetsorder-virkesorder/arbetsorder-virkesorder.models";
import {
    ArbetsorderAttributEventMessage,
    ArbetsorderAttributGrupper,
    ArbetsorderAttributVirkesorderValidationNycklar,
    GenereicMessageSeverity
} from "../events/ArbetsorderAttributEventMessage";
import { ArbetsorderOperationUpdatedEvent } from "../events/ArbetsorderOperationUpdatedEvent";
import { TransportavstandBerakningCompletedEventMessage } from "../events/TransportavstandBerakningCompletedEventMessage";
import { VirkesorderStatusChangedEventMessage } from "../events/VirkesorderStatusChangedEventMessage";
import { GetArbetsorderAlertsQueryResult } from "./arbetsorder.service";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderAlertService {
    private readonly apiClient = inject(ApiClientService);
    private readonly eventService = inject(EventService);

    getAlerts(arbetsorderId: number, unsubscribe$: Subject<void>): BehaviorSubject<IMessageModel[]> {
        const sources$ = [
            this.initAttributMessages(arbetsorderId, unsubscribe$),
            this.initAlerts(arbetsorderId, unsubscribe$)
        ];

        const result$ = new BehaviorSubject<IMessageModel[]>([]);

        const pulse$ = new Subject();
        pulse$
            .pipe(debounceTime(100))
            .subscribe(() => {
                const messages = flatMap(sources$, x => x.value);
                result$.next(messages);
            });

        sources$.forEach(x => x.subscribe(pulse$));

        return result$;
    }

    private initAlerts(arbetsorderId: number, unsubscribe$: Subject<void>): BehaviorSubject<IMessageModel[]> {
        const result$ = new BehaviorSubject<IMessageModel[]>([]);

        const emit = () => {
            this.apiClient
                .get<GetArbetsorderAlertsQueryResult[]>(`arbetsordererror/arbetsorder/${arbetsorderId}`)
                .subscribe(x => {
                    const messages = flatMap(x, x1 => x1.alerts)
                        .map(x1 => ({
                            severity: x1.severity,
                            message: x1.message,
                            anchor: undefined,
                            metadata: x1.metadata,
                            code: x1.code,
                            validationScope: x1.validationScope
                        }));

                    result$.next(messages);
                });
        };

        const throttle$ = new Subject();
        throttle$
            .pipe(debounceTime(100))
            .subscribe(() => emit());

        this.eventService.subscribeEvent<ArbetsorderOperationUpdatedEvent>(ArbetsorderOperationUpdatedEvent.$type)
            .pipe(takeUntil(unsubscribe$))
            .subscribe(throttle$);

        this.eventService.subscribeEvent<VirkesorderStatusChangedEventMessage>(VirkesorderStatusChangedEventMessage.$type)
            .pipe(takeUntil(unsubscribe$))
            .subscribe(throttle$);

        this.eventService.subscribeEvent<TransportavstandBerakningCompletedEventMessage>(TransportavstandBerakningCompletedEventMessage.$type)
            .pipe(takeUntil(unsubscribe$))
            .pipe(filter(event => event.arbetsorderId === arbetsorderId))
            .pipe(tap(event => this.transportavstandUppdaterat(event)))
            .subscribe(throttle$);

        this.eventService.subscribeAllArbetsorderSaga()
            .pipe(takeUntil(unsubscribe$))
            .pipe(filter(event => event.data.arbetsorderId === arbetsorderId))
            .subscribe(throttle$);

        emit();
        return result$;
    }

    private initAttributMessages(arbetsorderId: number, unsubscribe$: Subject<void>): BehaviorSubject<IMessageModel[]> {
        const result$ = new BehaviorSubject<IMessageModel[]>([]);

        const validationMessages$ = new BehaviorSubject<IMessageModel[]>([]);

        const emit = () => {
            const current = [...validationMessages$.value].filter(x => !!x.message);
            result$.next(current);
        };

        const aggregate$ = new Subject();
        aggregate$.pipe(debounceTime(50)).subscribe(() => emit());

        validationMessages$.subscribe(aggregate$);

        this.apiClient
            .get<ArbetsorderAttributEventMessage[]>(`Arbetsorder/${arbetsorderId}/attribut`)
            .pipe(map(x => x.filter(x1 => x1.grupp === ArbetsorderAttributGrupper.Viol3Validation || x1.grupp === ArbetsorderAttributGrupper.VirkesorderValidation)))
            .pipe(tap(x => this.checkValidationMessage(validationMessages$, x)))
            .subscribe();

        this.eventService.subscribeEvent<ArbetsorderAttributEventMessage>(ArbetsorderAttributEventMessage.$type)
            .pipe(takeUntil(unsubscribe$))
            .pipe(filter(x => x.arbetsorderId === arbetsorderId))
            .pipe(filter(x => x.grupp === ArbetsorderAttributGrupper.Viol3Validation || x.grupp === ArbetsorderAttributGrupper.VirkesorderValidation))
            .pipe(tap(x => this.checkValidationMessage(validationMessages$, [x])))
            .subscribe();

        return result$;
    }

    private transportavstandUppdaterat(x: TransportavstandBerakningCompletedEventMessage): void {
        this.apiClient
            .get<ArbetsorderAttributEventMessage[]>(`Arbetsorder/${x.arbetsorderId}/attribut`)
            .subscribe();
    }

    private checkValidationMessage(target$: BehaviorSubject<IMessageModel[]>, eventMessages: ArbetsorderAttributEventMessage[]): void {
        const validationAttribut = eventMessages
            .find(x => x.nyckel === ArbetsorderAttributVirkesorderValidationNycklar.GenericMessages);

        if (!validationAttribut) {
            return;
        }
        const genericMessages = JSON.parse(validationAttribut.varde) as GenericMessageRow[] ?? [];
        const messages: IMessageModel[] = genericMessages
            .map(x => ({
                severity: this.mapGenericMessageSeverity(x),
                message: x.message,
                anchor: undefined,
                metadata: { source: x.source },
                validationScope: x.validationScope
            }));

        target$.next(messages);
    }

    private mapGenericMessageSeverity(source: GenericMessageRow): MessageSeverity {
        if (source.code === "KrontvagvalSaknas") {
            return MessageSeverity.Info;
        }
        switch (source.severity) {
            case GenereicMessageSeverity.Critical:
                return MessageSeverity.Critical;
            case GenereicMessageSeverity.Error:
                return MessageSeverity.Error;
            case GenereicMessageSeverity.Warning:
                return MessageSeverity.Warning;
            case GenereicMessageSeverity.Info:
                return MessageSeverity.Info;
            default:
                return MessageSeverity.Info;
        }
    }
}
